import React from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Phone, Star, Wrench, ShieldCheck, Award, Heart } from 'lucide-react';

const values = [
  { icon: <Wrench size={22} />, title: 'Precision', desc: "We don't guess. Every job gets proper diagnosis, proper parts, and proper technique." },
  { icon: <ShieldCheck size={22} />, title: 'Honesty', desc: "You'll know what's wrong, why, and what it costs before any work starts. No surprises." },
  { icon: <Award size={22} />, title: 'Quality', desc: "We use the right parts and take the time to do the job correctly, because your safety depends on it." },
  { icon: <Heart size={22} />, title: 'Passion', desc: "We're riders ourselves. We understand what these machines mean to you." },
];

const css = `
  .about-page { background: #0d0d0d; }
  .about-story {
    max-width: 1180px; margin: 0 auto; padding: 80px 24px;
    display: grid; grid-template-columns: 1fr 1fr; gap: 72px; align-items: start;
  }
  .about-text p { color: #888; line-height: 1.85; margin-bottom: 18px; font-size: 0.95rem; }
  .about-text .btn-primary { margin-top: 12px; }
  .about-panel {
    background: #161616; border: 1px solid rgba(255,255,255,0.07);
    border-top: 3px solid #c0392b; padding: 36px; display: flex; flex-direction: column; gap: 24px;
  }
  .about-info { display: flex; gap: 14px; align-items: flex-start; }
  .about-info svg { color: #c0392b; flex-shrink: 0; margin-top: 2px; }
  .about-info strong {
    font-family: 'Oswald', sans-serif; font-size: 0.72rem; letter-spacing: 0.18em;
    text-transform: uppercase; color: #d4a017; display: block; margin-bottom: 4px;
  }
  .about-info span { font-size: 0.95rem; color: #bbb; }
  .about-info a { color: #bbb; text-decoration: none; transition: color 0.2s; }
  .about-info a:hover { color: #fff; }
  .review-box {
    background: rgba(192,57,43,0.08); border: 1px solid rgba(192,57,43,0.2); padding: 20px;
  }
  .review-stars { display: flex; gap: 4px; margin-bottom: 10px; }
  .review-quote { font-size: 0.93rem; color: #bbb; line-height: 1.65; font-style: italic; }
  .review-attr { margin-top: 10px; font-size: 0.8rem; color: #777; }

  .values-sec { background: #111; padding: 80px 0; }
  .values-inner { max-width: 1180px; margin: 0 auto; padding: 0 24px; }
  .values-grid { display: grid; grid-template-columns: repeat(4,1fr); gap: 22px; margin-top: 48px; }
  .val-card { background: #1a1a1a; border: 1px solid rgba(255,255,255,0.06); padding: 30px 22px; position: relative; overflow: hidden; }
  .val-card::after { content: ''; position: absolute; bottom: 0; left: 0; right: 0; height: 2px; background: linear-gradient(90deg, #c0392b, #d4a017); }
  .val-icon { width: 46px; height: 46px; background: rgba(192,57,43,0.1); display: flex; align-items: center; justify-content: center; color: #c0392b; margin-bottom: 18px; }
  .val-card h4 { font-size: 0.97rem; text-transform: uppercase; color: #fff; letter-spacing: 0.05em; margin-bottom: 10px; }
  .val-card p { font-size: 0.85rem; color: #888; line-height: 1.7; }

  .loc-sec { max-width: 1180px; margin: 0 auto; padding: 80px 24px; text-align: center; }
  .loc-sec h2 { margin-bottom: 12px; }
  .loc-sec p { color: #888; max-width: 480px; margin: 0 auto 36px; }
  .loc-actions { display: flex; justify-content: center; gap: 16px; flex-wrap: wrap; }

  @media (max-width:1024px) { .values-grid { grid-template-columns: repeat(2,1fr); } }
  @media (max-width:768px) { .about-story { grid-template-columns: 1fr; gap: 40px; } .values-grid { grid-template-columns: 1fr; } }
`;

export default function About() {
  return (
    <>
      <style>{css}</style>
      <div className="page-header">
        <div className="container">
          <span className="section-label">Who We Are</span>
          <h1>About Wengers Cycle Works</h1>
          <p>Real experience, honest work, and a genuine love for American V-Twins.</p>
        </div>
      </div>
      <div className="about-page">
        <div className="about-story">
          <div className="about-text">
            <span className="section-label">Our Story</span>
            <h2 className="section-title">Built on Real Experience</h2>
            <div className="divider" />
            <p>Wengers Cycle Works was built on one simple idea: do the work right, be honest about it, and treat every customer the way you'd want to be treated. No dealership nonsense. No upselling you on things you don't need. Just solid mechanical work done by people who actually care.</p>
            <p>Based in New Holland, PA, we specialize in American V-Twin platforms: repair, custom builds, and performance work. We've seen the inside of more engines than we can count, and that experience shows in everything we do.</p>
            <p>We also help riders and builders track down rare, hard-to-find, and discontinued parts. Finding the right part for an older build is something a lot of shops won't touch. We do.</p>
            <Link to="/contact" className="btn-primary"><Phone size={16} /> Get in Touch</Link>
          </div>
          <div>
            <div className="about-panel">
              <div className="about-info"><MapPin size={18} /><div><strong>Location</strong><span>New Holland, Pennsylvania</span></div></div>
              <div className="about-info"><Phone size={18} /><div><strong>Phone</strong><a href="tel:7172785897"><span>(717) 278-5897</span></a></div></div>
              <div className="about-info"><Wrench size={18} /><div><strong>Specialization</strong><span>American V-Twin: Repair, Builds, Performance</span></div></div>
              <div className="review-box">
                <div className="review-stars">{[...Array(5)].map((_,i) => <Star key={i} size={15} fill="#d4a017" color="#d4a017" />)}</div>
                <p className="review-quote">"Outstanding service and honest work. These guys know their V-Twins inside and out. Won't take my bikes anywhere else."</p>
                <p className="review-attr">Google Review</p>
              </div>
            </div>
          </div>
        </div>

        <div className="values-sec">
          <div className="values-inner">
            <span className="section-label">What Drives Us</span>
            <h2 className="section-title">Our Values</h2>
            <div className="divider" />
            <div className="values-grid">
              {values.map((v, i) => (
                <div className="val-card" key={i}>
                  <div className="val-icon">{v.icon}</div>
                  <h4>{v.title}</h4>
                  <p>{v.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="loc-sec">
          <span className="section-label">Find Us</span>
          <h2 className="section-title">New Holland, PA</h2>
          <p>Serving Lancaster County and surrounding areas. Call ahead before stopping by.</p>
          <div className="loc-actions">
            <a href="tel:7172785897" className="btn-primary"><Phone size={16} /> (717) 278-5897</a>
            <Link to="/contact" className="btn-outline"><MapPin size={16} /> Get in Touch</Link>
          </div>
        </div>
      </div>
    </>
  );
}
